/**
 * Import history page — list of past `ImportJob`s for a campaign with
 * status pills, counts, and per-row action buttons (Retry, Download
 * original, Download error report). One entry per upload — survives
 * across operator sessions so the audit trail is durable.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDateTime } from "@/lib/format";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { AppLayout } from "@/components/AppLayout";
import { BackLink } from "@/components/BackLink";
import { PermissionGate } from "@/components/PermissionGate";
import { ApiPaths, api } from "@/lib/api";
import type { ImportJob } from "@/lib/types";


const TONE: Record<string, string> = {
  succeeded: "bg-cvp-ok/20 text-cvp-ok",
  // Waiting on a person, not on us — and the ONLY row that carries an
  // action the import cannot finish without.
  awaiting_review: "bg-nb-mint/20 text-nb-mint",
  awaiting_mapping: "bg-cvp-warn/20 text-cvp-warn",
  processing: "bg-cvp-warn/20 text-cvp-warn",
  queued: "bg-cvp-warn/20 text-cvp-warn",
  failed_validation: "bg-cvp-danger/20 text-cvp-danger",
  failed_fatal: "bg-cvp-danger/20 text-cvp-danger",
  error: "bg-cvp-danger/20 text-cvp-danger",
};


export default function CampaignImportsPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const jobs = useQuery({
    queryKey: ["campaign-imports", id],
    queryFn: async () => {
      const res = await api.get<ImportJob[]>(ApiPaths.campaignImports(id!));
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: !!id,
    refetchInterval: (q) => {
      const data = q.state.data as ImportJob[] | undefined;
      if (!data) return 5000;
      const anyActive = data.some((j) =>
        ["queued", "processing"].includes(j.status),
      );
      return anyActive ? 2000 : false;
    },
  });

  const retry = useMutation({
    mutationFn: (jobId: string) =>
      api.post(ApiPaths.campaignImportRetry(id!, jobId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["campaign-imports", id] });
      void qc.invalidateQueries({ queryKey: ["campaign-leads", id] });
    },
  });

  // Both endpoints return `{"url": presigned}` JSON. We hit the API
  // with the bearer token (auth is required to *generate* the
  // presigned URL); the second hop to MinIO must NOT carry the
  // Authorization header (presigned signature + Authorization collide
  // and MinIO 4xxs). Plain `fetch` strips it, and we trigger the
  // download via an `<a download>` element so popup blockers (which
  // would kill a `window.open` after an `await`) can't fire.
  const downloadFile = async (endpoint: string, fallbackName: string) => {
    const res = await api.get<{ url: string }>(endpoint);
    if (!res.data?.url) return;
    const fileRes = await fetch(res.data.url);
    if (!fileRes.ok) return;
    const blob = await fileRes.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  };

  return (
    <AppLayout
      eyebrow={t("imports.eyebrow", "Campaign")}
      title={t("imports.title", "Import history")}
    >
      <BackLink
        fallback={`/campaigns/${id}`}
        className="text-sm text-nb-text-2 hover:underline"
      >
        ← {t("common.back", "Back")}
      </BackLink>

      <PermissionGate
        perm="campaign.read"
        fallback={
          <p className="card text-sm text-nb-text-2">
            {t("admin.noPermission", "You don't have permission")}
          </p>
        }
      >
        {jobs.isLoading && (
          <p className="card text-sm text-nb-text-3">
            {t("common.loading", "Loading…")}
          </p>
        )}
        {jobs.isError && (
          <p className="card text-sm text-cvp-danger">
            {t("imports.loadError", "Could not load the import history.")}
          </p>
        )}
        {jobs.data && jobs.data.length === 0 && (
          <p className="card text-sm text-nb-text-3">
            {t(
              "imports.empty",
              "No imports yet — upload an Excel file from the campaign page.",
            )}
          </p>
        )}
        {jobs.data && jobs.data.length > 0 && (
          <div className="card overflow-x-auto p-0">
            <table className="min-w-full text-sm">
              <thead className="bg-nb-surface/40 text-xs uppercase tracking-wide text-nb-text-3">
                <tr>
                  <th className="px-4 py-2 text-left">
                    {t("imports.col.when", "When")}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t("imports.col.file", "File")}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t("imports.col.status", "Status")}
                  </th>
                  <th className="px-4 py-2 text-left">
                    {t("imports.col.counts", "Counts")}
                  </th>
                  <th className="px-4 py-2 text-right">
                    {t("imports.col.actions", "Actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nb-border">
                {jobs.data.map((j) => (
                  <tr key={j.id}>
                    <td className="px-4 py-2 text-xs text-nb-text-2">
                      <div>
                        {formatDateTime(j.queued_at, {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                      </div>
                      {j.uploaded_by_email && (
                        <div className="text-nb-text-3">
                          {j.uploaded_by_email}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-nb-text">
                      <div className="truncate font-medium">
                        {j.source_filename || "—"}
                      </div>
                      <div className="font-mono text-[10px] text-nb-text-3">
                        {(j.source_size_bytes / 1024).toFixed(1)} KB ·{" "}
                        {j.source_sha256.slice(0, 12)}…
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={
                          "rounded px-1.5 py-0.5 text-[10px] uppercase " +
                          (TONE[j.status] || "bg-nb-surface text-nb-text-2")
                        }
                      >
                        {t(`leads.import.status.${j.status}`, j.status)}
                      </span>
                      {j.fatal_error && (
                        <div className="mt-1 text-[10px] text-cvp-danger">
                          {j.fatal_error}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs text-nb-text-2">
                      {j.status === "succeeded" && (
                        <>
                          <div>
                            {t("imports.col.memberships", "Memberships")}:{" "}
                            <strong className="text-nb-text">
                              {j.memberships_created}
                            </strong>
                          </div>
                          <div>
                            {t("imports.col.contacts", "Contacts")}:{" "}
                            <span className="text-nb-text-3">
                              +{j.contacts_created} / ~{j.contacts_updated}
                            </span>
                          </div>
                          {j.skipped_jsonb.length > 0 && (
                            <div className="text-cvp-warn">
                              {j.skipped_jsonb.length}{" "}
                              {t("imports.col.skipped", "skipped")}
                            </div>
                          )}
                        </>
                      )}
                      {j.status === "failed_validation" && (
                        <div className="text-cvp-danger">
                          {j.error_count}{" "}
                          {t("imports.col.errorRows", "error rows")} /{" "}
                          {j.row_count}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex flex-col items-end gap-1 text-xs">
                        <button
                          type="button"
                          onClick={() =>
                            void downloadFile(
                              ApiPaths.campaignImportSourceFile(id!, j.id),
                              j.source_filename || `import-${j.id}.xlsx`,
                            )
                          }
                          className="text-nb-mint hover:underline"
                        >
                          {t("imports.actions.original", "Original")}
                        </button>
                        {/* A parked job is finishable only from here
                            once the operator has navigated away — which
                            they will. Without this the import is
                            stranded, which is how the first real run
                            ended. */}
                        {j.status === "awaiting_review" && (
                          <Link
                            to={`/campaigns/${id}?tab=beneficiaries&review=${j.id}`}
                            className="text-nb-mint hover:underline"
                            data-testid="history-review-link"
                          >
                            {t("imports.actions.review")}
                          </Link>
                        )}
                        {j.has_error_file && (
                          <button
                            type="button"
                            onClick={() =>
                              void downloadFile(
                                ApiPaths.campaignImportErrorFile(id!, j.id),
                                `import-${j.id}-errors.xlsx`,
                              )
                            }
                            className="text-cvp-danger hover:underline"
                          >
                            {t(
                              "imports.actions.errorFile",
                              "Error report",
                            )}
                          </button>
                        )}
                        {[
                          "failed_validation",
                          "failed_fatal",
                          "error",
                        ].includes(j.status) && (
                          <button
                            type="button"
                            className="text-nb-text-2 hover:underline"
                            disabled={retry.isPending}
                            onClick={() => retry.mutate(j.id)}
                          >
                            {t("imports.actions.retry", "Retry")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PermissionGate>
    </AppLayout>
  );
}
